import React, { useState, useEffect } from "react";
import {
    getDevices,
    removeDevice,
    trustDevice
} from "../../api/authClient";
import { formatDate } from "../../utils/formatters";

export default function DeviceManagement() {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [actionLoading, setActionLoading] = useState(null);

    useEffect(() => {
        fetchDevices();
    }, []);

    const fetchDevices = async () => {
        try {
            setLoading(true);
            setError("");
            const data = await getDevices();
            setDevices(data.devices || []);
        } catch (err) {
            setError("Failed to load devices");
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveDevice = async (deviceId) => {
        if (!window.confirm("Remove this device? You'll need to log in again on that device.")) {
            return;
        }
        try {
            setActionLoading(deviceId);
            setError("");
            await removeDevice(deviceId);
            setSuccess("Device removed successfully");
            await fetchDevices();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to remove device");
        } finally {
            setActionLoading(null);
        }
    };

    const handleTrustDevice = async (deviceId) => {
        try {
            setActionLoading(deviceId);
            setError("");
            await trustDevice(deviceId);
            setSuccess("Device marked as trusted");
            await fetchDevices();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to trust device");
        } finally {
            setActionLoading(null);
        }
    };


    const getDeviceIcon = (device) => {
        const name = device.deviceName?.toLowerCase() || "";
        const os = device.operatingSystem?.toLowerCase() || "";

        if (name.includes("mobile") || name.includes("iphone") || name.includes("android") ||
            os.includes("ios") || os.includes("android")) {
            return (
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
            );
        }
        if (os.includes("mac") || os.includes("apple")) {
            return (
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
            );
        }
        return (
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
        );
    };

    if (loading) {
        return (
            <div className="card p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-gray-100 rounded w-1/3"></div>
                    <div className="h-24 bg-gray-100 rounded"></div>
                    <div className="h-24 bg-gray-100 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="card p-6">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-accent-50 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                </div>
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Recognized Devices</h2>
                    <p className="text-sm text-gray-500">
                        Devices that have accessed your account
                    </p>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-danger-500">{error}</p>
                </div>
            )}
            {success && (
                <div className="mb-4 p-3 bg-accent-50 border border-accent-200 rounded-lg">
                    <p className="text-sm text-accent-600">{success}</p>
                </div>
            )}

            {devices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <p>No devices recorded yet</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {devices.map((device) => (
                        <div
                            key={device.id}
                            className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                        >
                            <div className="flex items-start gap-4">
                                <div className="text-gray-500">
                                    {getDeviceIcon(device)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium text-gray-900">
                                            {device.deviceName || "Unknown Device"}
                                        </span>
                                        {device.trusted && (
                                            <span className="px-1.5 py-0.5 text-xs bg-accent-100 text-accent-600 rounded">
                                                Trusted
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                                        {device.browser && (
                                            <p>{device.browser} on {device.operatingSystem || "Unknown OS"}</p>
                                        )}
                                        <p>First seen: {formatDate(device.firstSeenAt) || "Unknown"}</p>
                                        <p>Last seen: {formatDate(device.lastSeenAt) || "Unknown"}</p>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2 items-end">
                                    {!device.trusted && (
                                        <button
                                            onClick={() => handleTrustDevice(device.id)}
                                            disabled={actionLoading === device.id}
                                            className="text-xs text-accent-600 hover:text-accent-700 disabled:opacity-50"
                                        >
                                            {actionLoading === device.id ? "..." : "Trust"}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleRemoveDevice(device.id)}
                                        disabled={actionLoading === device.id}
                                        className="text-xs text-danger-500 hover:text-danger-600 disabled:opacity-50"
                                    >
                                        {actionLoading === device.id ? "..." : "Remove"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">About Device Management</h3>
                <ul className="text-xs text-gray-500 space-y-1">
                    <li>New device logins will trigger an email alert</li>
                    <li>Trusted devices won't trigger login alerts</li>
                    <li>Removing a device will require re-authentication on next login</li>
                </ul>
            </div>
        </div>
    );
}
